<template>
  <div class="container-fluid py-3">

    <div class="card explorer-panel">

      <!-- Toolbar -->
      <div class="explorer-toolbar px-3 py-2 d-flex align-items-center gap-2">
        <i class="fa fa-search text-muted"></i>
        <input
          type="text"
          class="explorer-search form-control form-control-sm"
          placeholder="Buscar documentos..."
          v-model="searchQuery"
          @keyup.enter="buscar()"
        />
        <button class="btn btn-primary btn-sm" @click="buscar()">Buscar</button>
        <button v-if="searchQueryActiva" class="btn btn-outline-secondary btn-sm" @click="limpiarBusqueda()" title="Limpiar búsqueda">
          <i class="fa fa-times"></i>
        </button>
      </div>

      <div class="explorer-body">

        <!-- Cargando -->
        <div v-if="loading" class="text-center py-5">
          <div class="loader-box"><div class="loader-3"></div></div>
          <p class="text-muted small mt-2">Cargando...</p>
        </div>

        <!-- Error -->
        <div v-else-if="error" class="p-4">
          <div class="alert alert-danger mb-0">
            <strong>Error al cargar los datos.</strong> {{ error.message || 'Ocurrió un error inesperado' }}
            <button class="btn btn-sm btn-primary ms-3" @click="fetchTodo()">Reintentar</button>
          </div>
        </div>

        <template v-else>

          <!-- ── Vista búsqueda: lista plana ── -->
          <template v-if="searchQueryActiva">

            <div v-if="documentosFiltrados.length === 0" class="text-center py-5 text-muted small">
              No se encontraron documentos para "{{ searchQueryActiva }}".
            </div>

            <div id="documentos-list">
              <div
                v-for="doc in documentosPaginados"
                :key="doc.id"
                class="explorer-row file-row"
              >
                <i :class="[getDocumentIcon(doc.meta?.download_url), 'item-icon']"></i>
                <span class="item-name">{{ doc.title || 'Sin título' }}</span>
                <small v-if="doc.collection" class="item-meta text-muted">
                  <i class="fa fa-folder-o me-1"></i>{{ doc.collection.nombre }}
                </small>
                <div class="item-actions">
                  <router-link
                    :to="{ name: 'documentoDetalle', params: { id: doc.id } }"
                    class="btn btn-xs btn-outline-secondary"
                    title="Ver detalle"
                  ><i class="fa fa-eye"></i></router-link>
                  <a
                    :href="getDownloadUrl(doc.meta?.download_url)"
                    target="_blank"
                    class="btn btn-xs btn-outline-primary"
                    title="Descargar"
                  ><i class="fa fa-download"></i></a>
                </div>
              </div>
            </div>

            <!-- Paginación búsqueda -->
            <div v-if="totalPages > 1" class="d-flex justify-content-between align-items-center px-3 py-2 border-top">
              <span class="text-muted small">{{ rangoInicio }}–{{ rangoFin }} de {{ documentosFiltrados.length }}</span>
              <nav><ul class="pagination pagination-sm mb-0">
                <li class="page-item" :class="{ disabled: currentPage === 1 }">
                  <button class="page-link" @click="goToPage(currentPage - 1)">&laquo;</button>
                </li>
                <li v-for="page in visiblePages" :key="page" class="page-item" :class="{ active: page === currentPage, disabled: page === '…' }">
                  <button class="page-link" @click="page !== '…' && goToPage(page)">{{ page }}</button>
                </li>
                <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                  <button class="page-link" @click="goToPage(currentPage + 1)">&raquo;</button>
                </li>
              </ul></nav>
            </div>

          </template>

          <!-- ── Vista árbol ── -->
          <template v-else>

            <div v-if="arbolCarpetas.length === 0" class="text-center py-5 text-muted small">
              No hay documentos disponibles.
            </div>

            <template v-for="carpeta in arbolCarpetas" :key="carpeta.id">

              <!-- Carpeta raíz -->
              <div class="explorer-row folder-row" @click="toggleCarpeta(carpeta.id)">
                <i
                  class="toggle-icon fa fa-chevron-right"
                  :class="{ open: carpetasAbiertas[carpeta.id] }"
                ></i>
                <i
                  class="item-icon fa text-warning"
                  :class="carpetasAbiertas[carpeta.id] ? 'fa-folder-open' : 'fa-folder'"
                ></i>
                <span class="item-name fw-500">{{ carpeta.nombre }}</span>
                <span class="item-count">{{ totalDocsCarpeta(carpeta) }}</span>
              </div>

              <template v-if="carpetasAbiertas[carpeta.id]">

                <!-- Docs directos de la raíz -->
                <div
                  v-for="doc in carpeta.docs"
                  :key="doc.id"
                  class="explorer-row file-row lvl-1"
                >
                  <i :class="[getDocumentIcon(doc.meta?.download_url), 'item-icon']"></i>
                  <span class="item-name">{{ doc.title || 'Sin título' }}</span>
                  <div class="item-actions">
                    <router-link :to="{ name: 'documentoDetalle', params: { id: doc.id } }" class="btn btn-xs btn-outline-secondary" title="Ver detalle">
                      <i class="fa fa-eye"></i>
                    </router-link>
                    <a :href="getDownloadUrl(doc.meta?.download_url)" target="_blank" class="btn btn-xs btn-outline-primary" title="Descargar">
                      <i class="fa fa-download"></i>
                    </a>
                  </div>
                </div>

                <!-- Sub-carpetas -->
                <template v-for="hijo in carpeta.hijos" :key="hijo.id">

                  <div class="explorer-row folder-row lvl-1" @click="toggleCarpeta(hijo.id)">
                    <i
                      class="toggle-icon fa fa-chevron-right"
                      :class="{ open: carpetasAbiertas[hijo.id] }"
                    ></i>
                    <i
                      class="item-icon fa text-warning"
                      :class="carpetasAbiertas[hijo.id] ? 'fa-folder-open' : 'fa-folder'"
                    ></i>
                    <span class="item-name fw-500">{{ hijo.nombre }}</span>
                    <span class="item-count">{{ hijo.docs.length }}</span>
                  </div>

                  <div
                    v-if="carpetasAbiertas[hijo.id]"
                    v-for="doc in hijo.docs"
                    :key="doc.id"
                    class="explorer-row file-row lvl-2"
                  >
                    <i :class="[getDocumentIcon(doc.meta?.download_url), 'item-icon']"></i>
                    <span class="item-name">{{ doc.title || 'Sin título' }}</span>
                    <div class="item-actions">
                      <router-link :to="{ name: 'documentoDetalle', params: { id: doc.id } }" class="btn btn-xs btn-outline-secondary" title="Ver detalle">
                        <i class="fa fa-eye"></i>
                      </router-link>
                      <a :href="getDownloadUrl(doc.meta?.download_url)" target="_blank" class="btn btn-xs btn-outline-primary" title="Descargar">
                        <i class="fa fa-download"></i>
                      </a>
                    </div>
                  </div>

                </template>

              </template>

            </template>

          </template>

        </template>

      </div>
    </div>
  </div>
</template>

<script>
import api from "@/axios";

const PAGE_SIZE           = 20;
const COLECCIONES_OCULTAS = new Set(['Privados']);

export default {
  name: "Documentos",

  data() {
    return {
      colecciones:       [],
      documentos:        null,
      error:             null,
      loading:           true,
      searchQuery:       "",
      searchQueryActiva: "",
      currentPage:       1,
      carpetasAbiertas:  {},
    };
  },

  computed: {
    todosLosDocumentos() {
      return this.documentos?.items ?? [];
    },

    arbolCarpetas() {
      const docs = this.todosLosDocumentos;

      const visibles   = this.colecciones.filter(c => !COLECCIONES_OCULTAS.has(c.nombre));
      const idsOcultos = new Set(this.colecciones.filter(c => COLECCIONES_OCULTAS.has(c.nombre)).map(c => c.id));
      const colIds     = new Set(visibles.map(c => c.id));

      const nodos = {};
      for (const col of visibles) {
        nodos[col.id] = {
          id:     col.id,
          nombre: col.nombre,
          docs:   docs.filter(d => d.collection?.id === col.id),
          hijos:  [],
        };
      }

      const raices = [];
      for (const col of visibles) {
        if (col.padre_id === null) {
          raices.push(nodos[col.id]);
        } else if (nodos[col.padre_id]) {
          nodos[col.padre_id].hijos.push(nodos[col.id]);
        }
      }

      const tieneContenido = (n) => n.docs.length > 0 || n.hijos.some(h => h.docs.length > 0);
      const resultado = raices.filter(tieneContenido);

      const sinGrupo = docs.filter(d => (!d.collection || !colIds.has(d.collection.id)) && !idsOcultos.has(d.collection?.id));
      if (sinGrupo.length > 0) {
        resultado.push({ id: 'sin-carpeta', nombre: 'Sin carpeta', docs: sinGrupo, hijos: [] });
      }

      return resultado;
    },

    documentosFiltrados() {
      const q = this.searchQueryActiva.trim().toLowerCase();
      if (!q) return this.todosLosDocumentos;
      return this.todosLosDocumentos.filter(doc => (doc.title || '').toLowerCase().includes(q));
    },

    totalPages() {
      return Math.ceil(this.documentosFiltrados.length / PAGE_SIZE) || 1;
    },
    rangoInicio() {
      return (this.currentPage - 1) * PAGE_SIZE + 1;
    },
    rangoFin() {
      return Math.min(this.currentPage * PAGE_SIZE, this.documentosFiltrados.length);
    },
    documentosPaginados() {
      const start = (this.currentPage - 1) * PAGE_SIZE;
      return this.documentosFiltrados.slice(start, start + PAGE_SIZE);
    },
    visiblePages() {
      return this.calcVisiblePages(this.currentPage, this.totalPages);
    },
  },

  created() {
    this.fetchTodo();
  },

  methods: {
    calcVisiblePages(cur, total) {
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
      if (cur <= 4)         return [1, 2, 3, 4, 5, '…', total];
      if (cur >= total - 3) return [1, '…', total - 4, total - 3, total - 2, total - 1, total];
      return [1, '…', cur - 1, cur, cur + 1, '…', total];
    },

    totalDocsCarpeta(carpeta) {
      return carpeta.docs.length + carpeta.hijos.reduce((s, h) => s + h.docs.length, 0);
    },

    toggleCarpeta(id) {
      this.carpetasAbiertas = { ...this.carpetasAbiertas, [id]: !this.carpetasAbiertas[id] };
    },

    buscar() {
      this.searchQueryActiva = this.searchQuery;
      this.currentPage = 1;
    },

    limpiarBusqueda() {
      this.searchQuery       = "";
      this.searchQueryActiva = "";
      this.currentPage       = 1;
    },

    goToPage(page) {
      if (page < 1 || page > this.totalPages || page === this.currentPage) return;
      this.currentPage = page;
      document.getElementById('documentos-list')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },

    getDownloadUrl(url) {
      if (!url) return '#';
      return url.replace('http://localhost', 'https://rverdeadmin.klatus.systems');
    },

    getDocumentIcon(url) {
      if (!url) return 'fa fa-file-o text-secondary';
      const ext   = url.split('.').pop().toLowerCase();
      const icons = {
        pdf:  'fa fa-file-pdf-o text-danger',
        doc:  'fa fa-file-word-o text-primary',
        docx: 'fa fa-file-word-o text-primary',
        xls:  'fa fa-file-excel-o text-success',
        xlsx: 'fa fa-file-excel-o text-success',
        ppt:  'fa fa-file-powerpoint-o text-warning',
        pptx: 'fa fa-file-powerpoint-o text-warning',
        jpg:  'fa fa-file-image-o text-info',
        jpeg: 'fa fa-file-image-o text-info',
        png:  'fa fa-file-image-o text-info',
        zip:  'fa fa-file-archive-o text-secondary',
        rar:  'fa fa-file-archive-o text-secondary',
        txt:  'fa fa-file-text-o text-muted',
      };
      return icons[ext] || 'fa fa-file-o text-secondary';
    },

    async fetchTodo() {
      this.loading = true;
      this.error   = null;
      try {
        const [colResp, firstPage] = await Promise.all([
          api.get("colecciones/"),
          api.get("documents/?limit=20&offset=0"),
        ]);
        this.colecciones = colResp.data?.colecciones ?? [];

        const firstData  = firstPage.data;
        // Soporta meta.total_count, count o total según variante del API
        const totalCount = firstData?.meta?.total_count
                        ?? firstData?.count
                        ?? firstData?.total
                        ?? 0;
        let allItems = [...(firstData?.items ?? [])];

        // Solo pide más páginas si el total supera lo que ya tenemos
        if (totalCount > allItems.length) {
          const pagesLeft = Math.ceil(totalCount / 20) - 1;
          const requests  = Array.from({ length: pagesLeft }, (_, i) =>
            api.get(`documents/?limit=20&offset=${(i + 1) * 20}`)
          );
          const responses = await Promise.all(requests);
          for (const resp of responses) {
            allItems = allItems.concat(resp.data?.items ?? []);
          }
        }

        this.documentos = { meta: firstData?.meta, items: allItems };
      } catch (err) {
        this.error = err;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.explorer-panel {
  border-radius: 8px;
  overflow: hidden;
  font-size: 0.875rem;
}

.explorer-toolbar {
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.explorer-search {
  border: none;
  background: transparent;
  box-shadow: none !important;
  flex: 1;
}

.explorer-body {
  min-height: 200px;
}

/* ── filas ── */
.explorer-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 12px;
  min-height: 34px;
  border-bottom: 1px solid #f0f2f4;
  transition: background 0.1s;
}

.explorer-row:last-child {
  border-bottom: none;
}

.folder-row {
  cursor: pointer;
  background: #fafbfc;
}

.folder-row:hover {
  background: #eef2f7;
}

.file-row:hover {
  background: #f5f8ff;
}

/* ── chevron toggle ── */
.toggle-icon {
  font-size: 0.65rem;
  color: #adb5bd;
  width: 12px;
  flex-shrink: 0;
  transition: transform 0.15s;
}

.toggle-icon.open {
  transform: rotate(90deg);
}

/* ── iconos y texto ── */
.item-icon {
  font-size: 1rem;
  flex-shrink: 0;
  width: 18px;
  text-align: center;
}

.item-name {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.fw-500 { font-weight: 500; }

.item-meta {
  font-size: 0.78rem;
  white-space: nowrap;
  flex-shrink: 0;
}

.item-count {
  font-size: 0.75rem;
  color: #868e96;
  background: #e9ecef;
  border-radius: 10px;
  padding: 1px 7px;
  flex-shrink: 0;
}

/* ── acciones (visibles en hover) ── */
.item-actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.1s;
}

.explorer-row:hover .item-actions {
  opacity: 1;
}

.btn-xs {
  padding: 1px 6px;
  font-size: 0.75rem;
  line-height: 1.4;
}

/* ── indentación ── */
.lvl-1 { padding-left: 28px; }
.lvl-2 { padding-left: 52px; }
</style>
